import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';

// Define interfaces based on the API response structure
// Ensure these match the ones used in TeamMembers.tsx
interface Brand {
  id: string;
  name: string;
  role: 'reviewer' | 'writer' | 'admin' | 'manager';
  status: 'active' | 'pending';
  logo?: string;
}

interface Member {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  addedAt: string;
  brands: Brand[];
}

interface TeamMembersResponse {
  success: boolean;
  members: Member[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export const useTeamMembers = () => {
  return useQuery<TeamMembersResponse, Error>({ // Specify Error type for error handling
    queryKey: ['teamMembers'], // Unique key for this query
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (garbage collection time, replaces cacheTime)
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Get token using Firebase Auth
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        // Throw an error that useQuery will catch
        throw new Error('Authentication token is missing.'); 
      }

      // Fetch data from the correct endpoint
      const response = await fetch('https://api.socialness.ai/management/teams', {
        method: 'GET', // Use GET as per the curl command
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // No body needed for GET request
      });

      if (!response.ok) {
        // Try to get more detailed error from response body
        let errorBody = 'Failed to fetch team members.';
        try {
          const errorData = await response.json();
          errorBody = errorData.message || JSON.stringify(errorData) || errorBody;
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(`HTTP error ${response.status}: ${errorBody}`);
      }

      const data = await response.json();

      // Basic validation of the response structure
      if (!data.success || !Array.isArray(data.members)) {
         throw new Error('Invalid API response structure received.');
      }

      return data; // Return the full response object
    },
  });
}; 